// frontend/src/themes/decor/ThemeDecor.jsx
// Camada de decoração dos temas elaborados. Renderiza null quando o tema
// ativo não tem decorId → custo zero nos demais 10 temas.
import React from "react";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import { GlobalStyles } from "@mui/material";
import { DECOR_KEYFRAMES } from "./animations";
import catFace from "../assets/cat-face.svg";
import catWalk from "../assets/cat-walk.svg";
import rocket from "../assets/rocket.svg";
import planet from "../assets/planet.svg";

// Patinhas: SVG próprio como data-URI, repetido com opacidade baixa.
const PAW = encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='96' height='96' viewBox='0 0 96 96'>` +
  `<g fill='#E5679E'><ellipse cx='34' cy='30' rx='7' ry='9'/><ellipse cx='50' cy='24' rx='6' ry='8'/>` +
  `<ellipse cx='64' cy='30' rx='6' ry='8'/><path d='M49 60c-11 0-19-7-19-14 0-6 8-9 19-9s19 3 19 9c0 7-8 14-19 14z'/></g></svg>`
);
const pawPattern = `url("data:image/svg+xml,${PAW}")`;

// Estrelas: campos de box-shadow em pontos pseudo-aleatórios determinísticos.
const stars = (n, seed) => {
  const out = [];
  for (let i = 1; i <= n; i++) {
    const x = (i * seed * 97) % 100;
    const y = (i * seed * 53) % 100;
    out.push(`${x}vw ${y}vh 0 0 rgba(228,230,245,0.8)`);
  }
  return out.join(", ");
};

const layerBase = {
  position: "fixed",
  inset: 0,
  pointerEvents: "none",
  zIndex: 0,
  overflow: "hidden",
};

const Gatinhos = ({ variant }) => (
  <div data-decor="gatinhos" style={layerBase}>
    <div style={{ position: "absolute", inset: 0, backgroundImage: pawPattern, backgroundSize: 96, opacity: 0.04 }} />
    {variant === "app" && (
      <>
        <img src={catFace} alt="" width={40} style={{ position: "absolute", left: 12, bottom: 8, opacity: 0.9 }} />
        <img
          src={catWalk} alt="" height={30}
          className="kr-decor-anim"
          style={{ position: "absolute", bottom: 0, left: 0, animation: "kr-catwalk 300s linear infinite", willChange: "transform" }}
        />
      </>
    )}
    {variant === "login" && (
      <img src={catFace} alt="" width={96}
        className="kr-decor-anim"
        style={{ position: "absolute", right: "12%", top: "16%", animation: "kr-float 6s ease-in-out infinite" }} />
    )}
  </div>
);

const Galaxia = ({ variant }) => (
  <div data-decor="galaxia" style={layerBase}>
    <div className="kr-decor-anim" style={{ position: "absolute", width: 1, height: 1, borderRadius: "50%", boxShadow: stars(70, 7), animation: "kr-twinkle 4s ease-in-out infinite", willChange: "opacity" }} />
    <div className="kr-decor-anim" style={{ position: "absolute", width: 2, height: 2, borderRadius: "50%", boxShadow: stars(30, 13), animation: "kr-twinkle 6s ease-in-out 2s infinite", willChange: "opacity" }} />
    <div className="kr-decor-anim" style={{
      position: "absolute", top: "8%", right: "6%", width: 3, height: 3, borderRadius: "50%",
      background: "#FFF", boxShadow: "0 0 6px 2px rgba(255,255,255,0.8)",
      animation: "kr-shooting 220s linear 20s infinite", willChange: "transform",
    }} />
    {variant === "login" && (
      <>
        <img src={rocket} alt="" width={80} className="kr-decor-anim"
          style={{ position: "absolute", right: "14%", top: "20%", animation: "kr-float 7s ease-in-out infinite" }} />
        <img src={planet} alt="" width={56}
          style={{ position: "absolute", left: "10%", bottom: "14%", opacity: 0.85 }} />
      </>
    )}
  </div>
);

const DECORS = { gatinhos: Gatinhos, galaxia: Galaxia };

const ThemeDecor = ({ variant = "app" }) => {
  const theme = useTheme();
  const decorId = theme.custom?.decorId;
  const Decor = decorId ? DECORS[decorId] : null;
  if (!Decor) return null;
  return (
    <>
      <GlobalStyles styles={DECOR_KEYFRAMES} />
      <Decor variant={variant} />
    </>
  );
};

ThemeDecor.propTypes = { variant: PropTypes.oneOf(["app", "login"]) };
Gatinhos.propTypes = { variant: PropTypes.string };
Galaxia.propTypes = { variant: PropTypes.string };

export default React.memo(ThemeDecor);
